import { useTheme, Box, Typography } from '@mui/material';
import { CardInfoProps } from './CardInfo.props';
import { Card } from '@/components/molecules/Card';
import { ColumnLayout } from '@/components/atoms/ColumnLayout';
import { RowLayout } from '@/components/atoms/RowLayout';
import { RadioButton } from '@/components/atoms/RadioButton';
import {
  getDefaultSizeConfig,
  getSelectableStyleConfig,
  getSelectableNoRadioStyleConfig,
} from './CardInfo.config';

/**
 * CardInfo - Unified information card component for displaying icon, title, and description
 *
 * Features:
 * - Icon container with primary color scheme
 * - Centered title and description
 * - Configurable border style (dashed/solid/none)
 * - Configurable background color
 * - Optional clickable/selectable behavior with checkbox
 * - Configurable layout: 'column' (default) or 'row'
 * - Size variants: small, medium (default), large
 * - Customizable icon size, max width, title and description sizes
 * - Default variant: dashed border, transparent background
 * - Selectable variant: solid border, white background, selection state with checkbox
 */
export const CardInfo = ({
  title,
  description,
  icon,
  spacing = '2xl',
  layout = 'column',
  sx,
  value,
  selected = false,
  onSelect,
  borderStyle,
  backgroundColor,
  variant = 'default',
  size = 'medium',
  iconSize,
  maxWidth,
  titleSize,
  descriptionSize,
  grow = false,
  selectableLayoutProps,
}: CardInfoProps) => {
  const theme = useTheme();

  // Get size and style configurations
  const sizeConfig = getDefaultSizeConfig(theme);
  const selectableStyle = getSelectableStyleConfig(theme);
  const selectableNoRadioStyle = getSelectableNoRadioStyleConfig(theme);

  const config = sizeConfig[size];

  // Determine if this is a selectable card (either explicitly or via onSelect prop)
  const isSelectable =
    variant === 'selectable' || variant === 'selectableNoRadio' || !!onSelect;
  const isSelectableNoRadio = variant === 'selectableNoRadio';

  // Determine border style: solid for selectable, dashed for default (unless overridden)
  const effectiveBorderStyle =
    borderStyle || (isSelectable ? 'solid' : 'dashed');

  // Icon container for selectable variant - specific dimensions
  const selectableIconContainer = (
    <Box
      sx={{
        width: selectableStyle.iconContainer.width,
        height: selectableStyle.iconContainer.height,
        borderRadius: selectableStyle.iconContainer.borderRadius,
        padding: selectableStyle.iconContainer.padding,
        backgroundColor: selectableStyle.colors.iconContainerBackground(
          theme.palette
        ),
        border: `1px solid ${selectableStyle.colors.iconContainerBorder(theme.palette)}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& > *': {
          color: selectableStyle.colors.iconColor(theme.palette),
          width: selectableStyle.iconContainer.iconSize,
          height: 'auto',
          opacity: 1,
          transform: 'rotate(0deg)',
        },
      }}
    >
      {icon}
    </Box>
  );

  // Icon container with styling for default variant
  const iconContainer = (
    <Box
      sx={{
        width: config.iconContainer,
        height: config.iconContainer,
        borderRadius: theme.spacing(1.25), // 10px
        padding: config.iconPadding,
        backgroundColor:
          theme.palette.Primary?.['0'] ||
          (theme.palette.primary as any)?.light ||
          theme.palette.background?.paper,
        border: `1px solid ${theme.palette.Primary?.['50'] || (theme.palette.primary as any)?.light || theme.palette.divider}`,
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
        '& > *': {
          color:
            theme.palette.Primary?.['300'] ||
            (theme.palette.primary as any)?.main ||
            theme.palette.text?.primary,
          fontSize: iconSize || config.iconSize,
          width: iconSize || config.iconSize,
          height: iconSize || config.iconSize,
        },
      }}
    >
      {icon}
    </Box>
  );

  // Content with title and description
  const cardContent = (
    <ColumnLayout
      spacing={description ? 'sm' : 'xs'}
      alignItems="center"
      sx={{ textAlign: 'center' }}
    >
      <Typography
        sx={{
          ...theme.typography.M3?.body?.large,
          fontWeight: 500,
          fontSize: titleSize || config.titleSize,
          lineHeight: '155%',
          color:
            theme.palette.Neutral?.['900'] ||
            theme.palette.text?.primary ||
            theme.palette.grey?.[900],
        }}
      >
        {title}
      </Typography>
      {description && (
        <Typography
          sx={{
            ...theme.typography.M3?.body?.large,
            fontSize: descriptionSize || config.descriptionSize,
            lineHeight: '150%',
            color:
              theme.palette.Neutral?.['500'] ||
              theme.palette.text?.secondary ||
              theme.palette.grey?.[500],
          }}
        >
          {description}
        </Typography>
      )}
    </ColumnLayout>
  );

  // Default variant (non-selectable) - use layout prop to determine RowLayout or ColumnLayout
  if (!isSelectable) {
    const LayoutComponent = layout === 'row' ? RowLayout : ColumnLayout;
    const defaultContent = (
      <LayoutComponent
        spacing={spacing}
        alignItems="center"
        sx={{ textAlign: 'center' }}
      >
        {iconContainer}
        {cardContent}
      </LayoutComponent>
    );

    const outlineColor =
      theme.palette.M3?.sys?.light?.['outline 3'] ||
      theme.palette.divider ||
      theme.palette.grey?.[300];
    const borderValue =
      borderStyle === 'none'
        ? 'none'
        : `2px ${effectiveBorderStyle} ${outlineColor}`;

    return (
      <Card
        elevation={0}
        content={defaultContent}
        sx={{
          ...(grow && { flex: 1 }),
          maxWidth: maxWidth || config.maxWidth,
          padding: config.padding,
          borderRadius: theme.spacing(1.5), // 12px
          backgroundColor: backgroundColor || 'transparent',
          border: borderValue,
          textAlign: 'center',
          // Override default Card theme styling - only boxShadow and overflow
          '&.MuiCard-root': {
            boxShadow: 'none',
            overflow: 'visible',
          },
          // Override CardContent padding
          '& .MuiCardContent-root': {
            padding: 0,
            '&:last-child': {
              paddingBottom: 0,
            },
          },
          ...sx,
        }}
      />
    );
  }

  // Selectable variant - row layout with icon and column layout for title/description with checkbox
  const selectableContent = (
    <RowLayout
      spacing={
        selectableLayoutProps?.mainLayoutSpacing ||
        selectableStyle.mainLayout.spacing
      }
      alignItems={
        selectableLayoutProps?.mainLayoutAlignItems ||
        selectableStyle.mainLayout.alignItems
      }
      sx={{
        width: selectableStyle.mainLayout.width,
        minHeight: selectableStyle.mainLayout.minHeight,
        gap:
          selectableLayoutProps?.mainLayoutGap ||
          selectableStyle.mainLayout.gap,
      }}
    >
      {selectableIconContainer}
      <ColumnLayout
        spacing={
          selectableLayoutProps?.contentLayoutSpacing ||
          selectableStyle.contentLayout.spacing
        }
        sx={{
          width: selectableStyle.contentLayout.width,
          minHeight: selectableStyle.contentLayout.minHeight,
          flex: 1,
          gap:
            selectableLayoutProps?.contentLayoutGap ||
            selectableStyle.contentLayout.gap,
        }}
      >
        <RowLayout
          spacing={
            selectableLayoutProps?.titleRowSpacing ||
            selectableStyle.titleRow.spacing
          }
          alignItems={
            selectableLayoutProps?.titleRowAlignItems ||
            selectableStyle.titleRow.alignItems
          }
          sx={{
            width: selectableStyle.titleRow.width,
            height: selectableStyle.titleRow.height,
            justifyContent:
              selectableLayoutProps?.titleRowJustifyContent ||
              selectableStyle.titleRow.justifyContent,
            gap:
              selectableLayoutProps?.titleRowGap ||
              selectableStyle.titleRow.gap,
          }}
        >
          <Typography
            sx={{
              ...theme.typography.M3?.title?.medium,
              color: selectableStyle.colors.titleColor(theme.palette),
            }}
          >
            {title}
          </Typography>
          {!isSelectableNoRadio && (
            <RadioButton checked={selected} sx={{ padding: 0 }} />
          )}
        </RowLayout>
        {description && (
          <Typography
            sx={{
              width: selectableStyle.description.width,
              minHeight: selectableStyle.description.minHeight,
              ...theme.typography.M3?.body?.medium,
              color: selectableStyle.colors.descriptionColor(theme.palette),
              textAlign:
                selectableLayoutProps?.descriptionTextAlign ||
                selectableStyle.description.textAlign,
            }}
          >
            {description}
          </Typography>
        )}
      </ColumnLayout>
    </RowLayout>
  );

  // SelectableNoRadio variant - row layout with icon and column layout for title/description without radio button
  const selectableNoRadioContent = (
    <RowLayout
      spacing={
        selectableLayoutProps?.mainLayoutSpacing ||
        selectableNoRadioStyle.mainLayout.spacing
      }
      alignItems={
        selectableLayoutProps?.mainLayoutAlignItems ||
        selectableNoRadioStyle.mainLayout.alignItems
      }
      sx={{
        width: selectableNoRadioStyle.mainLayout.width,
        minHeight: selectableNoRadioStyle.mainLayout.minHeight,
        gap:
          selectableLayoutProps?.mainLayoutGap ||
          selectableNoRadioStyle.mainLayout.gap,
      }}
    >
      {selectableIconContainer}
      <ColumnLayout
        spacing={
          selectableLayoutProps?.contentLayoutSpacing ||
          selectableNoRadioStyle.contentLayout.spacing
        }
        sx={{
          width: selectableNoRadioStyle.contentLayout.width,
          minHeight: selectableNoRadioStyle.contentLayout.minHeight,
          gap:
            selectableLayoutProps?.contentLayoutGap ||
            selectableNoRadioStyle.contentLayout.gap,
        }}
      >
        <RowLayout
          spacing={
            selectableLayoutProps?.titleRowSpacing ||
            selectableNoRadioStyle.titleRow.spacing
          }
          alignItems={
            selectableLayoutProps?.titleRowAlignItems ||
            selectableNoRadioStyle.titleRow.alignItems
          }
          sx={{
            width: selectableNoRadioStyle.titleRow.width,
            height: selectableNoRadioStyle.titleRow.height,
            justifyContent:
              selectableLayoutProps?.titleRowJustifyContent ||
              selectableNoRadioStyle.titleRow.justifyContent,
            gap:
              selectableLayoutProps?.titleRowGap ||
              selectableNoRadioStyle.titleRow.gap,
          }}
        >
          <Typography
            sx={{
              ...theme.typography.M3?.title?.medium,
              color: selected
                ? selectableNoRadioStyle.colors.selectedTitleColor(
                    theme.palette
                  )
                : selectableNoRadioStyle.colors.titleColor(theme.palette),
            }}
          >
            {title}
          </Typography>
        </RowLayout>
        {description && (
          <Typography
            sx={{
              width: selectableNoRadioStyle.description.width,
              minHeight: selectableNoRadioStyle.description.minHeight,
              ...theme.typography.M3?.body?.medium,
              color: selectableNoRadioStyle.colors.descriptionColor(
                theme.palette
              ),
              textAlign:
                selectableLayoutProps?.descriptionTextAlign ||
                selectableNoRadioStyle.description.textAlign,
            }}
          >
            {description}
          </Typography>
        )}
      </ColumnLayout>
    </RowLayout>
  );

  const defaultBackgroundColor =
    backgroundColor || selectableStyle.colors.background(theme.palette);
  const defaultBorderColor = selected
    ? selectableStyle.colors.selectedBorder(theme.palette)
    : selectableStyle.colors.border(theme.palette);
  const borderValue = `${selectableStyle.card.borderWidth} solid ${defaultBorderColor}`;

  // SelectableNoRadio variant
  if (isSelectableNoRadio) {
    const noRadioBackgroundColor =
      backgroundColor ||
      selectableNoRadioStyle.colors.background(theme.palette);
    const noRadioBorderColor = selected
      ? selectableNoRadioStyle.colors.selectedBorder(theme.palette)
      : selectableNoRadioStyle.colors.border(theme.palette);
    const noRadioBorderValue = `${selectableNoRadioStyle.card.borderWidth} solid ${noRadioBorderColor}`;

    return (
      <Card
        elevation={0}
        content={selectableNoRadioContent}
        onClick={onSelect && value ? () => onSelect(value) : undefined}
        sx={{
          ...(grow && { flex: 1 }),
          maxWidth: maxWidth || selectableNoRadioStyle.card.maxWidth,
          maxHeight: selectableNoRadioStyle.card.maxHeight,
          paddingLeft: selectableNoRadioStyle.card.paddingLeft,
          paddingTop: selectableNoRadioStyle.card.paddingTop,
          borderRadius: selectableNoRadioStyle.card.borderRadius,
          backgroundColor: noRadioBackgroundColor,
          border: noRadioBorderValue,
          cursor: onSelect ? 'pointer' : 'default',
          textAlign: 'left',
          // Override default Card theme styling
          '&.MuiCard-root': {
            boxShadow: 'none',
            overflow: 'visible',
          },
          // Override CardContent padding
          '& .MuiCardContent-root': {
            padding: selectableNoRadioStyle.cardContent.padding(theme),
            '&:last-child': {
              paddingBottom: selectableNoRadioStyle.cardContent.padding(theme),
            },
          },
          ...sx,
        }}
      />
    );
  }

  return (
    <Card
      elevation={0}
      content={selectableContent}
      onClick={onSelect && value ? () => onSelect(value) : undefined}
      sx={{
        ...(grow && { flex: 1 }),
        maxWidth: maxWidth || selectableStyle.card.maxWidth,
        maxHeight: selectableStyle.card.maxHeight,
        paddingLeft: selectableStyle.card.paddingLeft,
        paddingTop: selectableStyle.card.paddingTop,
        borderRadius: selectableStyle.card.borderRadius,
        backgroundColor: defaultBackgroundColor,
        border: borderValue,
        cursor: onSelect ? 'pointer' : 'default',
        textAlign: 'left',
        // Override default Card theme styling
        '&.MuiCard-root': {
          boxShadow: 'none',
          overflow: 'visible',
        },
        // Override CardContent padding
        '& .MuiCardContent-root': {
          padding: selectableStyle.cardContent.padding(theme),
          '&:last-child': {
            paddingBottom: selectableStyle.cardContent.padding(theme),
          },
        },
        ...sx,
      }}
    />
  );
};

export default CardInfo;
